
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface SubscriptionContextType {
  subscribed: boolean;
  subscriptionTier: string | null;
  subscriptionEnd: string | null;
  loading: boolean;
  checkSubscription: () => Promise<void>;
  createCheckout: (plan: 'monthly' | 'annual') => Promise<void>;
  openCustomerPortal: () => Promise<void>;
  isPremium: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { user, session } = useAuth();
  const { toast } = useToast();

  const checkSubscription = async () => {
    // Only check if we have a session - don't reset if no session during loading
    if (!session) {
      console.log('No session available, skipping subscription check');
      return;
    }

    try {
      console.log('🔄 Checking subscription status...');
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.warn('⚠️ Subscription check returned error:', error.message);
        // Treat all errors gracefully - user is simply not subscribed
        setSubscribed(false);
        setSubscriptionTier(null);
        setSubscriptionEnd(null);
        return;
      }

      console.log('✅ Subscription status:', data);
      setSubscribed(data.subscribed || false);
      setSubscriptionTier(data.subscription_tier || null);
      setSubscriptionEnd(data.subscription_end || null);
    } catch (error) {
      console.error('❌ Error in checkSubscription:', error);
      // Treat any caught error as unsubscribed to prevent blocking the UI
      setSubscribed(false);
      setSubscriptionTier(null);
      setSubscriptionEnd(null);
    } finally {
      setLoading(false);
    }
  };

  const createCheckout = async (plan: 'monthly' | 'annual') => {
    if (!session) {
      toast({
        title: "Error",
        description: "Debes iniciar sesión para suscribirte",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('🔄 Creating checkout session for plan:', plan);
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('❌ Error creating checkout:', error);
        toast({
          title: "Error",
          description: "No se pudo crear la sesión de pago",
          variant: "destructive",
        });
        return;
      }

      console.log('✅ Checkout URL created:', data.url);
      // Open Stripe checkout in a new tab
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('❌ Error in createCheckout:', error);
      toast({
        title: "Error",
        description: "Error al procesar el pago",
        variant: "destructive",
      });
    }
  };

  const openCustomerPortal = async () => {
    if (!session) {
      toast({
        title: "Error",
        description: "Debes iniciar sesión para gestionar tu suscripción",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('🔄 Opening customer portal...');
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('❌ Error opening customer portal:', error);
        toast({
          title: "Error",
          description: "No se pudo abrir el portal de cliente",
          variant: "destructive",
        });
        return;
      }

      console.log('✅ Customer portal URL created:', data.url);
      // Open customer portal in a new tab
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('❌ Error in openCustomerPortal:', error);
      toast({
        title: "Error",
        description: "Error al abrir el portal de cliente",
        variant: "destructive",
      });
    }
  };

  const isPremium = subscribed;

  useEffect(() => {
    if (session) {
      console.log('🔄 Session detected, checking subscription...');
      checkSubscription();
    } else if (user === null) {
      // Only reset when explicitly logged out (user is null), not during loading
      console.log('🔄 User logged out, resetting subscription state');
      setSubscribed(false);
      setSubscriptionTier(null);
      setSubscriptionEnd(null);
      setLoading(false);
    }
  }, [user, session]);

  // Auto-refresh subscription status every 30 seconds when on page
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      checkSubscription();
    }, 30000);

    return () => clearInterval(interval);
  }, [user]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscribed,
        subscriptionTier,
        subscriptionEnd,
        loading,
        checkSubscription,
        createCheckout,
        openCustomerPortal,
        isPremium,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};
